export type PlanATripMapBounds = {
  topLeftLat: string;
  topLeftLng: string;
  bottomRightLat: string;
  bottomRightLng: string;
};

type LatLng = { lat: number; lng: number };

export type TripPlannerSearchArea = {
  mapBounds?: PlanATripMapBounds;
  // fallback if bounds are not available
  mapCenter?: LatLng;
};

/** How far a search area has to be from the user before we consider it a trip rather than a local outing. */
export const tripPlannerMinimumDistanceMiles = 75;

const earthRadiusMiles = 3958.8;

const toRadians = (degrees: number) => (degrees * Math.PI) / 180;

export const getDistanceInMiles = (from: LatLng, to: LatLng) => {
  const latDelta = toRadians(to.lat - from.lat);
  const lngDelta = toRadians(to.lng - from.lng);
  const a = Math.sin(latDelta / 2) ** 2 + Math.cos(toRadians(from.lat)) * Math.cos(toRadians(to.lat)) * Math.sin(lngDelta / 2) ** 2;
  return 2 * earthRadiusMiles * Math.asin(Math.min(1, Math.sqrt(a)));
};

const parseCoordinate = (value: string) => {
  const coordinate = Number(value);
  return value.trim() && Number.isFinite(coordinate) ? coordinate : undefined;
};

export const getSearchAreaCenter = ({ mapBounds, mapCenter }: TripPlannerSearchArea): LatLng | undefined => {
  if (mapBounds) {
    const topLeftLat = parseCoordinate(mapBounds.topLeftLat);
    const topLeftLng = parseCoordinate(mapBounds.topLeftLng);
    const bottomRightLat = parseCoordinate(mapBounds.bottomRightLat);
    const bottomRightLng = parseCoordinate(mapBounds.bottomRightLng);
    if (topLeftLat !== undefined && topLeftLng !== undefined && bottomRightLat !== undefined && bottomRightLng !== undefined) {
      return { lat: (topLeftLat + bottomRightLat) / 2, lng: (topLeftLng + bottomRightLng) / 2 };
    }
  }
  return mapCenter;
};

type IsTripPlannerSearchAreaFarAwayArgs = TripPlannerSearchArea & { userLocation?: LatLng };

/**
 * The trip planner is meant for planning trips away from home, so its entry points are hidden when the search area is
 * close to the user. When we don't know where the user is (or where they're searching), we default to showing them.
 */
export const isTripPlannerSearchAreaFarAway = ({ mapBounds, mapCenter, userLocation }: IsTripPlannerSearchAreaFarAwayArgs) => {
  const searchAreaCenter = getSearchAreaCenter({ mapBounds, mapCenter });
  if (!searchAreaCenter || !userLocation) {
    return true;
  }
  return getDistanceInMiles(userLocation, searchAreaCenter) >= tripPlannerMinimumDistanceMiles;
};
